import {
  Box,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useState } from "react";
import { AppModalDialogBox } from "../../../components/modals";
import { SaveAndCancelButton } from "../../../components/buttons";
import FlexBox from "../../../components/layouts/flexbox";
import {
  HOLIDAY_TYPES,
  Holiday,
  HolidayFormValues,
  HolidayType,
} from "./types";
import {
  buildHolidayPayload,
  emptyHolidayForm,
  holidayToFormValues,
  validateHolidayForm,
} from "./utils";

interface HolidayFormModalProps {
  holiday?: Holiday | null;
  onClose: () => void;
  onSubmit: (payload: Record<string, any>) => Promise<void> | void;
}

function HolidayFormModal({ holiday, onClose, onSubmit }: HolidayFormModalProps) {
  const isEdit = Boolean(holiday);
  const [form, setForm] = useState<HolidayFormValues>(() =>
    holiday ? holidayToFormValues(holiday) : emptyHolidayForm()
  );
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateField = <K extends keyof HolidayFormValues>(
    key: K,
    value: HolidayFormValues[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError("");
  };

  const handleTypeChange = (type: HolidayType) => {
    setForm((prev) => ({
      ...prev,
      type,
      month: type === HOLIDAY_TYPES.ANNUAL ? prev.month : "",
      day: type === HOLIDAY_TYPES.ANNUAL ? prev.day : "",
      date: type === HOLIDAY_TYPES.ONE_TIME ? prev.date : "",
    }));
    setError("");
  };

  const handleSave = async () => {
    const validationError = validateHolidayForm(form);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(buildHolidayPayload(form));
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedDate = form.date ? dayjs(form.date) : null;

  return (
    <AppModalDialogBox
      isModalOpen={true}
      title={isEdit ? "Edit Holiday" : "Add Holiday"}
      handleClose={onClose}
      size="sm"
      actionButtonRenderer={
        <SaveAndCancelButton
          primaryText={isEdit ? "Update" : "Add"}
          defaultText="Cancel"
          onPrimaryActionClick={handleSave}
          onDefaultButtonClick={onClose}
          isPrimaryButtonDisabled={isSubmitting}
        />
      }
    >
      <FlexBox flexDirection="column" gap={2.5} p={1}>
        <Box>
          <Typography sx={labelSx}>Name</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Holiday name"
            value={form.name}
            onChange={(e) => updateField("name", e.target.value)}
          />
        </Box>

        <Box>
          <Typography sx={labelSx}>Type</Typography>
          <FormControl>
            <RadioGroup
              row
              value={form.type}
              onChange={(e) => handleTypeChange(e.target.value as HolidayType)}
            >
              <FormControlLabel
                value={HOLIDAY_TYPES.ANNUAL}
                control={<Radio size="small" />}
                label="Annual"
              />
              <FormControlLabel
                value={HOLIDAY_TYPES.ONE_TIME}
                control={<Radio size="small" />}
                label="One-time"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        {form.type === HOLIDAY_TYPES.ANNUAL ? (
          <FlexBox gap={2}>
            <Box flex={1}>
              <Typography sx={labelSx}>Month</Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={form.month}
                onChange={(e) => updateField("month", e.target.value)}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <MenuItem key={m} value={String(m)}>
                    {m}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box flex={1}>
              <Typography sx={labelSx}>Day</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="1–31"
                value={form.day}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^\d{1,2}$/.test(value)) {
                    updateField("day", value);
                  }
                }}
              />
            </Box>
          </FlexBox>
        ) : (
          <Box>
            <Typography sx={labelSx}>Date</Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={selectedDate}
                onChange={(date) => {
                  updateField("date", date ? date.format("YYYY-MM-DD") : "");
                }}
                renderInput={(params) => (
                  <TextField {...params} size="small" fullWidth />
                )}
              />
            </LocalizationProvider>
          </Box>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={form.active}
              onChange={(_, checked) => updateField("active", checked)}
              color="primary"
            />
          }
          label="Active"
        />

        {error ? (
          <Typography color="error" fontSize={13}>
            {error}
          </Typography>
        ) : null}
      </FlexBox>
    </AppModalDialogBox>
  );
}

const labelSx = {
  fontSize: 14,
  fontWeight: 500,
  color: "#070639",
  mb: 1,
};

export { HolidayFormModal };
